import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Button, Drawer, Form, Input, Select, Space, Switch, Table, Tag, Typography, App, } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiError } from '@/lib/api';
import { useAuthStore } from '@/lib/auth-store';
const TYPE_LABEL = {
    ASSET: 'Aset',
    LIABILITY: 'Liabilitas',
    NET_ASSET: 'Aset Bersih',
    REVENUE: 'Pendapatan',
    EXPENSE: 'Beban',
};
const TYPE_COLOR = {
    ASSET: 'blue',
    LIABILITY: 'gold',
    NET_ASSET: 'purple',
    REVENUE: 'green',
    EXPENSE: 'volcano',
};
export function CoaPage() {
    const { message } = App.useApp();
    const qc = useQueryClient();
    const hasPerm = useAuthStore((s) => s.hasPerm);
    const [drawer, setDrawer] = useState({ open: false });
    const [form] = Form.useForm();
    const q = useQuery({
        queryKey: ['coa'],
        queryFn: async () => (await api.get('/accounts')).data,
    });
    const createMut = useMutation({
        mutationFn: async (v) => (await api.post('/accounts', v)).data,
        onSuccess: () => {
            message.success('Akun ditambahkan');
            qc.invalidateQueries({ queryKey: ['coa'] });
            setDrawer({ open: false });
        },
        onError: (e) => message.error(apiError(e)),
    });
    const patchMut = useMutation({
        mutationFn: async ({ id, body }) => (await api.patch(`/accounts/${id}`, body)).data,
        onSuccess: () => {
            message.success('Akun diperbarui');
            qc.invalidateQueries({ queryKey: ['coa'] });
            setDrawer({ open: false });
        },
        onError: (e) => message.error(apiError(e)),
    });
    const cols = [
        { title: 'Kode', dataIndex: 'code', width: 120 },
        { title: 'Nama Akun', dataIndex: 'name' },
        {
            title: 'Kategori',
            dataIndex: 'account_type',
            width: 140,
            render: (t) => _jsx(Tag, { color: TYPE_COLOR[t], children: TYPE_LABEL[t] ?? t }),
        },
        { title: 'Saldo Normal', dataIndex: 'normal_balance', width: 120 },
        {
            title: 'Status',
            dataIndex: 'is_active',
            width: 100,
            render: (a) => (a ? _jsx(Tag, { color: "success", children: "Aktif" }) : _jsx(Tag, { children: "Nonaktif" })),
        },
        {
            title: 'Aksi',
            width: 100,
            render: (_, r) => hasPerm('coa:edit') && (_jsx(Button, { size: "small", icon: _jsx(EditOutlined, {}), onClick: () => {
                    setDrawer({ open: true, row: r });
                    form.setFieldsValue(r);
                } })),
        },
    ];
    return (_jsxs("div", { children: [_jsxs(Space, { style: { marginBottom: 16, display: 'flex', justifyContent: 'space-between' }, children: [_jsx(Typography.Title, { level: 3, style: { margin: 0 }, children: "Bagan Akun (Chart of Accounts)" }), hasPerm('coa:create') && (_jsx(Button, { type: "primary", icon: _jsx(PlusOutlined, {}), onClick: () => {
                            form.resetFields();
                            setDrawer({ open: true });
                        }, children: "Tambah Akun" }))] }), _jsx(Table, { rowKey: "id", loading: q.isLoading, dataSource: q.data ?? [], columns: cols, size: "middle", pagination: { pageSize: 30 } }), _jsx(Drawer, { open: drawer.open, onClose: () => setDrawer({ open: false }), title: drawer.row ? `Edit ${drawer.row.code}` : 'Tambah Akun', width: 480, destroyOnClose: true, extra: _jsx(Button, { type: "primary", loading: createMut.isPending || patchMut.isPending, onClick: () => {
                        form.validateFields().then((v) => {
                            if (drawer.row) {
                                patchMut.mutate({ id: drawer.row.id, body: v });
                            }
                            else {
                                createMut.mutate(v);
                            }
                        });
                    }, children: "Simpan" }), children: _jsxs(Form, { form: form, layout: "vertical", preserve: false, children: [_jsx(Form.Item, { name: "code", label: "Kode", rules: [{ required: true }], children: _jsx(Input, { placeholder: "mis. 411.004", disabled: !!drawer.row }) }), _jsx(Form.Item, { name: "name", label: "Nama Akun", rules: [{ required: true }], children: _jsx(Input, {}) }), _jsx(Form.Item, { name: "account_type", label: "Kategori", rules: [{ required: true }], children: _jsx(Select, { disabled: !!drawer.row, options: Object.entries(TYPE_LABEL).map(([v, l]) => ({ value: v, label: l })) }) }), _jsx(Form.Item, { name: "normal_balance", label: "Saldo Normal", rules: [{ required: true }], children: _jsx(Select, { disabled: !!drawer.row, options: [
                                    { value: 'DEBIT', label: 'Debit' },
                                    { value: 'CREDIT', label: 'Kredit' },
                                ] }) }), drawer.row && (_jsx(Form.Item, { name: "is_active", label: "Aktif", valuePropName: "checked", children: _jsx(Switch, {}) }))] }) })] }));
}
